import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NphiesEligibilityResultDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { cn, dateOnly, display } from '@/lib/utils';
import { Button, Field, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { EligibilityChip, type EligibilityFacts } from '../insurance/EligibilityChip';
import { filterOffered, offeredPayerIds, useBranchPayers } from '../insurance/flow/branch-payers';
import {
  bookingDayOf,
  choiceStillOffered,
  coverageValidOn,
  defaultPayerChoice,
  insuranceChoice,
  insuranceChoiceFor,
  samePayerChoice,
  SELF_PAY,
  type LastVisitPayer,
  type PayerChoice,
  type PayerMode,
} from './payer-choice';

export interface PayerPickerProps {
  patientId: string;
  date: string;
  at?: string | null;
  value: PayerChoice;
  onChange: (next: PayerChoice) => void;
  current?: LastVisitPayer | null;
}

interface CoverageRow extends EligibilityFacts {
  id: string;
  payerId: string;
  policyNo: string;
  validFrom: string | null;
  validTo: string | null;
  isActive: boolean;
  deletedAt?: string | null;
  payer: { id: string; name: string; nphiesLicense?: string | null };
}

function coverageLabel(coverage: CoverageRow): string {
  const validTo = coverage.validTo ? ` · ${tr('opd', 'valid_to_x', { date: dateOnly(coverage.validTo) })}` : '';
  return `${coverage.payer.name} · ${coverage.policyNo}${validTo}`;
}

function PayerModeSwitch({ mode, disabled, onChange }: { mode: PayerMode; disabled: boolean; onChange: (next: PayerMode) => void }) {
  const options: { id: PayerMode; label: string }[] = [
    { id: 'self', label: tr('opd', 'self_pay') },
    { id: 'insurance', label: tr('opd', 'insurance') },
  ];
  return (
    <div className="inline-flex rounded-md border bg-background p-0.5" role="group" aria-label={tr('opd', 'payer')}>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          disabled={disabled}
          aria-pressed={mode === option.id}
          onClick={() => onChange(option.id)}
          className={cn(
            'rounded px-3 py-1 text-sm font-medium transition-colors focus-ring disabled:opacity-50',
            mode === option.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function CheckEligibilityButton({ patientId, coverage }: { patientId: string; coverage: CoverageRow }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const check = useMutation({
    mutationFn: () => post<NphiesEligibilityResultDto>('/integrations/nphies/eligibility', { patientId, coverageId: coverage.id }),
    onSuccess: (answer) => {
      const detail = answer.warnings.join(' ') || undefined;
      if (answer.eligible) toast.success(answer.message, detail);
      else toast.error(answer.message, detail);
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['patient-coverages', patientId] });
    },
    onError: (caught) => toast.error(tr('opd', 'eligibility_check_failed'), describeError(caught)),
  });
  return (
    <Button size="xs" variant="outline" loading={check.isPending} onClick={() => check.mutate()}>
      {tr('opd', 'check_eligibility')}
    </Button>
  );
}

export function PayerPicker({ patientId, date, at, value, onChange, current }: PayerPickerProps) {
  const { hasModule, can } = useAuth();
  const [chosenFor, setChosenFor] = useState<string | null>(null);
  const dayKey = `${patientId}|${date}`;
  const chosen = chosenFor === dayKey;

  const patient = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => get<{ coverages: CoverageRow[] }>(`/patients/${patientId}`),
    enabled: Boolean(patientId),
  });
  const askLastVisit = current === undefined;
  const lastVisit = useQuery({
    queryKey: ['last-visit-payer', patientId],
    queryFn: () => get<LastVisitPayer | null>(`/appointments/last-payer${qs({ patientId })}`),
    enabled: Boolean(patientId) && askLastVisit,
    staleTime: 60_000,
  });

  const coverages = patient.data?.coverages ?? [];
  const bookingDay = bookingDayOf(date, at, display.timezone);
  const offeredPayers = useBranchPayers(Boolean(patientId));
  const offered = offeredPayerIds(offeredPayers.data);
  const validOnDay = coverages.filter((coverage) => coverageValidOn(coverage, bookingDay));
  const valid = filterOffered(validOnDay, offered);
  const hiddenByBranch = validOnDay.length - valid.length;
  const earlier = askLastVisit ? lastVisit.data : current;
  const fallback = defaultPayerChoice(valid, earlier);
  const loaded = Boolean(patient.data) && (!askLastVisit || lastVisit.isFetched) && (offeredPayers.isFetched || offeredPayers.isError);
  const keepChoice = chosen && choiceStillOffered(value, valid);

  useEffect(() => {
    if (!loaded || keepChoice) return;
    if (!samePayerChoice(fallback, value)) onChange(fallback);
  }, [loaded, keepChoice, fallback.mode, fallback.payerId, fallback.coverageId, value.mode, value.payerId, value.coverageId, onChange]);

  const switchMode = (mode: PayerMode) => {
    setChosenFor(dayKey);
    onChange(mode === 'self' ? SELF_PAY : insuranceChoiceFor(valid));
  };

  const pickCoverage = (coverageId: string) => {
    setChosenFor(dayKey);
    const cover = valid.find((coverage) => coverage.id === coverageId);
    onChange(cover ? insuranceChoice(cover) : insuranceChoiceFor([]));
  };

  const selected = value.coverageId ? valid.find((coverage) => coverage.id === value.coverageId) : undefined;
  const fromLastVisit = !chosen && loaded && fallback.mode === 'insurance' && Boolean(fallback.coverageId) && samePayerChoice(fallback, value);
  const notValidToday = coverages.length - validOnDay.length;
  const mayAskNphies = hasModule('nphies') && can('nphies.eligibility');
  const onNphies = Boolean(selected?.payer.nphiesLicense);

  return (
    <div className="space-y-2">
      <Field label={tr('opd', 'payer')} hint={fromLastVisit ? tr('opd', 'payer_from_last_visit') : undefined} hintBelow>
        <PayerModeSwitch mode={value.mode} disabled={!loaded} onChange={switchMode} />
      </Field>
      {value.mode === 'insurance' && (
        <Field
          label={tr('opd', 'coverage')}
          required
          hint={valid.length === 0 ? (hiddenByBranch > 0 ? tr('billing', 'nx_coverage_not_offered_here') : tr('opd', 'no_coverage_valid_on_this_day')) : hiddenByBranch > 0 ? tr('billing', 'nx_coverage_not_offered_here') : notValidToday > 0 ? tr('opd', 'coverages_not_valid_on_this_day') : undefined}
          hintBelow
        >
          <Select value={value.coverageId ?? ''} disabled={!loaded || valid.length === 0} placeholder={tr('opd', 'pick_a_coverage')} onChange={(event) => pickCoverage(event.target.value)}>
            {valid.map((coverage) => (
              <option key={coverage.id} value={coverage.id}>
                {coverageLabel(coverage)}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {selected && (
        <div className="flex flex-wrap items-center gap-2">
          <EligibilityChip coverage={selected} />
          {onNphies && mayAskNphies && <CheckEligibilityButton patientId={patientId} coverage={selected} />}
        </div>
      )}
    </div>
  );
}

export function renderPayerField(props: PayerPickerProps) {
  return <PayerPicker {...props} />;
}
